import _ from "lodash";
import PropTypes from "prop-types";
import { Paper, createTheme, ThemeProvider } from "@mui/material";
import baseTheme from "../themes/baseTheme";

const grey = baseTheme.palette.grey;
const blue = baseTheme.palette.blue;
const green = baseTheme.palette.green;
const orange = baseTheme.palette.orange;
const red = baseTheme.palette.red;

const defaultStyles = {
  display: "inline-flex",
  fontFamily: "Inter Medium",
  textTransform: "none",
  padding: "2px 10px",
  fontSize: "14px",
  lineHeight: "20px",
  "& svg + span": {
    marginLeft: "9.67px",
  },
  "& span + svg": {
    marginLeft: "9.67px",
  },
  "&.icon-only": {
    padding: "8px 12px",
  },
  "& svg": {
    stroke: grey[0],
  },
};

const sizes = {
  default: _.cloneDeep(defaultStyles),
  small: _.merge(_.cloneDeep(defaultStyles), {
    padding: "2px 8px",
    "&.icon-only": {
      padding: "8px",
    },
  }),
  large: _.merge(_.cloneDeep(defaultStyles), {
    padding: "4px 12px",
  }),
  xlarge: _.merge(_.cloneDeep(defaultStyles), {
    padding: "8px 12px",
    "& svg": {
      width: "16px",
    },
  }),
};

const roles = {
  default: {
    color: grey[0],
    backgroundColor: blue[50],
  },
  secondary: {
    color: blue[50],
    backgroundColor: blue[30],
    borderColor: blue[30],
    "& svg": {
      stroke: blue[50],
    },
  },
  tertiary: {
    color: grey[90],
    backgroundColor: grey[20],
    borderColor: grey[20],
    "& svg": {
      stroke: grey[80],
    },
  },
  success: {
    color: grey[0],
    backgroundColor: green[50],
    "& svg": {
      stroke: grey[0],
    },
  },
  successLight: {
    color: green[60],
    backgroundColor: green[10],
    "& svg": {
      stroke: green[60],
    },
  },
  error: {
    color: grey[0],
    backgroundColor: red[50],
    "& svg": {
      stroke: grey[0],
    },
  },
  errorLight: {
    color: red[60],
    backgroundColor: red[10],
    "& svg": {
      stroke: red[60],
    },
  },
  warning: {
    color: grey[0],
    backgroundColor: orange[50],
    "& svg": {
      stroke: grey[0],
    },
  },
  warningLight: {
    color: orange[60],
    backgroundColor: orange[10],
    "& svg": {
      stroke: orange[60],
    },
  },
};

function getTheme(size, role) {
  const sizeTheme = _.cloneDeep(sizes[size] || sizes["default"]);
  const roleTheme = _.cloneDeep(roles[role] || roles["default"]);
  return createTheme({
    components: {
      MuiPaper: {
        styleOverrides: {
          root: _.merge(sizeTheme, roleTheme),
        },
      },
    },
  });
}

export default function Label({
  children,
  size,
  role,
  variant,
  Icon,
  iconWidth = "12px",
}) {
  return (
    <ThemeProvider theme={getTheme(size, role)}>
      <Paper
        elevation={0}
        className={variant === "iconOnly" ? "icon-only" : ""}
      >
        {variant === "leadingIcon" ? (
          <>
            <Icon width={iconWidth} />
            <span>{children}</span>
          </>
        ) : variant === "trailingIcon" ? (
          <>
            <span>{children}</span>
            <Icon width={iconWidth} />
          </>
        ) : variant === "iconOnly" ? (
          <Icon width={iconWidth} />
        ) : (
          <>{children}</>
        )}
      </Paper>
    </ThemeProvider>
  );
}

Label.propTypes = {
  /**
   * Button label
   */
  text: PropTypes.string,
  /**
   * Button size
   */
  size: PropTypes.string,
  /**
   * Button variant: '' or 'leadingIcon' or 'trailingIcon' or 'iconOnly'
   */
  variant: PropTypes.string,
  /**
   * Icon: Component name
   */
  icon: PropTypes.func,
  /**
   * Icon width in px
   */
  iconWidth: PropTypes.string,
};

Label.defaultProps = {
  size: "default",
  role: "default",
  disabled: false,
};
